import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../components/ui';
import type { CycleRef } from '../../../api/plan-options';
import type { TreatmentStage } from '../../../types/admin';
import { useCreatePlanOption } from '../hooks/usePlanOptions';
import { PlanOptionCard } from './plan-options/PlanOptionCard';
import { PlanRail } from './plan-options/PlanRail';
import styles from './plan-options/PlanOptions.module.scss';
import { planTitleMap, sortPlanOptions } from './plan-options/pricing';

export type PlanOptionsSectionProps = {
  readonly patientId: string;
  readonly treatmentId: string;
  readonly stage: TreatmentStage;
};

function tabId(optionId: string): string {
  return `plan-${optionId}-tab`;
}

function panelId(optionId: string): string {
  return `plan-${optionId}-panel`;
}

/**
 * The per-cycle list of treatment plans the patient compares in the app.
 *
 * Plans are picked from a rail rather than stacked: one editor is visible at a
 * time, while the rail carries each plan's counts, total, STL progress and
 * unsaved-edit state. Every editor stays mounted (hidden panels), so switching
 * plans never drops a half-typed draft.
 */
export function PlanOptionsSection({ patientId, treatmentId, stage }: PlanOptionsSectionProps) {
  const { t } = useTranslation();

  const cycle = useMemo<CycleRef>(
    () => ({ patientId, treatmentId, cycleId: stage.id }),
    [patientId, treatmentId, stage.id],
  );
  const createMutation = useCreatePlanOption(cycle);

  // Fixtures and pre-plan-option cached payloads can omit the list entirely.
  const options = useMemo(() => sortPlanOptions(stage.plan_options ?? []), [stage.plan_options]);
  const hasOptions = options.length > 0;
  const alternativeCount = options.filter((option) => !option.is_main).length;

  const titles = useMemo(
    () =>
      planTitleMap(options, {
        main: t('planOptions.mainPlanTitle'),
        alternative: (index: number) => t('planOptions.alternativePlanTitle', { index }),
      }),
    [options, t],
  );

  const [activeId, setActiveId] = useState<string | null>(null);
  const [dirtyIds, setDirtyIds] = useState<ReadonlySet<string>>(() => new Set<string>());
  const knownIdsRef = useRef<ReadonlySet<string>>(new Set(options.map((option) => option.id)));

  // A freshly created plan is what the admin wants to fill in next.
  useEffect(() => {
    const created = options.find((option) => !knownIdsRef.current.has(option.id));
    knownIdsRef.current = new Set(options.map((option) => option.id));
    if (created) setActiveId(created.id);
  }, [options]);

  const activeOption =
    options.find((option) => option.id === activeId) ??
    options.find((option) => option.is_selected) ??
    options[0] ??
    null;

  const handleDirtyChange = useCallback((optionId: string, isDirty: boolean) => {
    setDirtyIds((previous) => {
      if (previous.has(optionId) === isDirty) return previous;
      const next = new Set(previous);
      if (isDirty) next.add(optionId);
      else next.delete(optionId);
      return next;
    });
  }, []);

  function handleAdd() {
    createMutation.mutate({});
  }

  return (
    <div className={styles.section}>
      <header className={styles.sectionHeader}>
        <div className={styles.sectionHeaderText}>
          <h4 className={styles.sectionTitle}>{t('planOptions.title')}</h4>
          <p className={styles.sectionSubtitle}>{t('planOptions.subtitle')}</p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleAdd}
          disabled={createMutation.isPending}
        >
          {hasOptions ? t('planOptions.addAlternative') : t('planOptions.addMain')}
        </Button>
      </header>

      {createMutation.error instanceof Error && (
        <p className={styles.inlineError}>{createMutation.error.message}</p>
      )}

      {!hasOptions && <p className={styles.emptyState}>{t('planOptions.empty')}</p>}

      {hasOptions && (
        <div className={styles.plansLayout}>
          <PlanRail
            options={options}
            titles={titles}
            activeId={activeOption?.id ?? null}
            dirtyIds={dirtyIds}
            onSelect={setActiveId}
            tabId={tabId}
            panelId={panelId}
          />

          <div className={styles.planPanes}>
            {options.map((option) => (
              <div
                key={option.id}
                id={panelId(option.id)}
                role="tabpanel"
                aria-labelledby={tabId(option.id)}
                hidden={option.id !== activeOption?.id}
                className={styles.planPane}
              >
                <PlanOptionCard
                  cycle={cycle}
                  stage={stage}
                  option={option}
                  displayTitle={titles.get(option.id) ?? ''}
                  canDelete={!option.is_main || alternativeCount === 0}
                  isActive={option.id === activeOption?.id}
                  onDirtyChange={(isDirty) => handleDirtyChange(option.id, isDirty)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
